import {useCallback, useEffect, useRef, useState} from 'react';

import {useI18n} from '@yourcoach/i18n';

import type {AlertProps, ConfirmProps, CurrentAlert, Props} from './types';

import SuccessSvg from '../../../../assets/icons/primary/Tick Square.svg';
import {emitter} from '../../../../utils/eventEmitter';
import {platform} from '../../../../utils/platform';
import {ALERT_HIDE_EVENT, ALERT_SHOW_EVENT} from '../../utils';
import {usePlatformController} from './controller.platform';
import {processAlertProps} from './utils';

export const useController = ({animationDuration}: Props) => {
  const {t} = useI18n();
  const [isShown, setIsShown] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const [currentAlert, setCurrentAlert] = useState<CurrentAlert>();

  const isMounted = useRef(false);
  const queue = useRef<CurrentAlert[]>([]);
  const hideTimeout = useRef(0);
  const isShownRef = useRef(false);
  const autoHideTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const currentAlertRef = useRef<CurrentAlert | null>(null);

  const {bottomOffset, onBeforeUpdate, onHide, onShow} =
    usePlatformController();

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (platform.isWeb) {
      document.body.style.overflow = isShown ? 'hidden' : 'auto';
    }
  }, [isShown]);

  const hide = useCallback(() => {
    if (!isShownRef.current || isHiding) {
      return;
    }

    if (autoHideTimeoutRef.current != null) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }

    currentAlertRef.current?.resolve?.(undefined);
    currentAlertRef.current = null;

    setIsHiding(true);

    hideTimeout.current = +setTimeout(() => {
      onHide();

      setCurrentAlert(undefined);
      setIsShown(false);
      isShownRef.current = false;
      setIsHiding(false);

      emitter.emit(ALERT_HIDE_EVENT);
    }, animationDuration);
  }, [animationDuration, isHiding, onHide]);

  const scheduleAutoHide = useCallback(
    (alert: CurrentAlert) => {
      if (autoHideTimeoutRef.current != null) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }

      if (alert.autoHideAfterMs != null && alert.autoHideAfterMs > 0) {
        autoHideTimeoutRef.current = setTimeout(() => {
          autoHideTimeoutRef.current = null;
          hide();
        }, alert.autoHideAfterMs);
      }
    },
    [hide],
  );

  const show = useCallback(
    (alert: AlertProps) => {
      return new Promise<void>(resolve => {
        if (!isMounted.current) {
          resolve(undefined);

          return;
        }

        alert = processAlertProps(alert);

        if (isShownRef.current) {
          queue.current.push({...alert, resolve});

          return;
        }

        setIsShown(true);

        isShownRef.current = true;

        onShow();

        const current = {...alert, resolve};

        setCurrentAlert(current);
        currentAlertRef.current = current;
        scheduleAutoHide(current);

        emitter.emit(ALERT_SHOW_EVENT);
      });
    },
    [onShow, scheduleAutoHide],
  );

  const update = useCallback(
    (alert: AlertProps = {}) => {
      if (!isShownRef.current) {
        return;
      }

      alert = processAlertProps(alert);

      onBeforeUpdate();

      setCurrentAlert(prev => {
        const next = {...alert, resolve: prev!.resolve};

        currentAlertRef.current = next;

        return next;
      });
    },
    [onBeforeUpdate],
  );

  useEffect(() => {
    const handleHide = () => {
      const nextAlert = queue.current.shift();

      if (nextAlert) {
        // prevent automatic batching in React 18
        setTimeout(() => {
          setIsShown(true);
          isShownRef.current = true;

          onShow();

          setCurrentAlert(nextAlert);
          currentAlertRef.current = nextAlert;
          scheduleAutoHide(nextAlert);

          emitter.emit(ALERT_SHOW_EVENT);
        }, 100);
      }
    };

    emitter.on(ALERT_HIDE_EVENT, handleHide);

    return () => emitter.off(ALERT_HIDE_EVENT, handleHide);
  }, [onShow, scheduleAutoHide]);

  useEffect(() => {
    return () => {
      clearTimeout(hideTimeout.current);

      if (autoHideTimeoutRef.current != null) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }
    };
  }, []);

  const clearQueue = useCallback(
    (hideDisplayedAlert?: boolean) => {
      queue.current = [];

      if (hideDisplayedAlert) {
        hide();
      }
    },
    [hide],
  );

  const getCurrentAlert = useCallback(() => currentAlert, [currentAlert]);

  const confirm = useCallback(
    (alert?: ConfirmProps) => {
      const title = alert?.title || t('shared:message.confirm');

      const buttons: Required<AlertProps<boolean>>['buttons'] =
        alert?.buttons?.length === 2
          ? [
              {
                label: alert.buttons[0]!,
                onAwaitableClick: resolve => resolve(true),
                scheme: alert.confirmButtonScheme || 'primary',
              },
              {
                label: alert.buttons[1]!,
                onAwaitableClick: resolve => resolve(false),
              },
            ]
          : [
              {
                label: t('shared:yes'),
                onAwaitableClick: resolve => resolve(true),
                scheme: alert?.confirmButtonScheme || 'primary',
              },
              {
                label: t('shared:no'),
                onAwaitableClick: resolve => resolve(false),
              },
            ];

      return show({
        ...alert,
        // @ts-expect-error: TODO: describe why
        buttons,
        title,
      });
    },

    [show, t],
  );

  const showError = useCallback(
    (error: Error, isFixable = false) => {
      return show({
        message: error.message,
        title: t(`shared:message.error${isFixable ? '_fix' : ''}`),
      });
    },
    [show, t],
  );

  const success = useCallback(
    (alert?: AlertProps) => {
      show({
        icon: SuccessSvg,
        iconColor: 'icon.primary',
        iconSize: 72,
        title: t('shared:message.success'),
        ...alert,
      });
    },
    [show, t],
  );

  return {
    bottomOffset,
    clearQueue,
    confirm,
    currentAlert,
    getCurrentAlert,
    hide,
    isHiding,
    isShown,
    show,
    showError,
    success,
    update,
  };
};
